/**
 * Framework-free lifecycle event bus.
 *
 * Lets any code (including future React components) subscribe to
 * application lifecycle events without depending on jQuery.
 *
 * Every `emit` is additionally re-broadcast through whatever "legacy
 * mirror" function has been registered via `setLegacyMirror` (see
 * `global.js`, which wires this up to the existing
 * `$(page_state).trigger(...)` mechanism) so that custom QSL scripts
 * and existing `$(page_state).on(...)` listeners keep working unchanged.
 *
 * Note: A subscriber or the legacy mirror throwing does not prevent the
 * other from running.
 */

const listeners = {};
let legacyMirror = null;

/**
 * Subscribe to an event.
 *
 * @param {string} name The event name.
 * @param {Function} fn The listener to call when the event fires.
 */
function on(name, fn) {
  if (!listeners[name]) {
    listeners[name] = [];
  }

  listeners[name].push(fn);
}

/**
 * Unsubscribe a listener.
 *
 * @param {string} name The event name.
 * @param {Function} fn The listener to remove.
 */
function off(name, fn) {
  if (!listeners[name]) {
    return;
  }

  listeners[name] = listeners[name].filter(function (registered) {
    return registered !== fn;
  });
}

/**
 * Emit an event to bus subscribers, then to the legacy mirror (if one has
 * been configured).
 *
 * @param {string} name The event name.
 * @param {...*} args Arguments to pass to subscribers and the legacy mirror.
 */
function emit(name, ...args) {
  (listeners[name] || []).forEach(function (fn) {
    try {
      fn.apply(null, args);
    } catch (err) {
      console.error('Gryphon.events: subscriber for "' + name + '" threw', err);
    }
  });

  if (legacyMirror) {
    legacyMirror(name, args);
  }
}

/**
 * Used to re-broadcast emits through the legacy jQuery mechanism.
 *
 * @param {Function} fn Called as `fn(name, args)` on every emit.
 */
function setLegacyMirror(fn) {
  legacyMirror = fn;
}

Gryphon.events = {
  emit,
  on,
  off,
  setLegacyMirror,
};
